import {
  startTransition,
  type ComponentProps,
  type FocusEventHandler,
  type MouseEventHandler,
  type PointerEventHandler,
} from 'react';
import { Link, useNavigate, type To } from 'react-router-dom';
import { prefetchRoute } from '../routing/prefetchRoute';

type RouterLinkProps = ComponentProps<typeof Link>;

type PrefetchLinkProps = RouterLinkProps & {
  LinkComponent?: typeof Link;
};

function pathFromTo(to: To): string {
  if (typeof to === 'string') return to;
  const pathname = to.pathname ?? '';
  const search = typeof to.search === 'string' ? to.search : '';
  const hash = typeof to.hash === 'string' ? to.hash : '';
  return `${pathname}${search}${hash}` || '/';
}

/**
 * Router link that warms the lazy page chunk on hover/focus/mousedown, then
 * navigates inside `startTransition` so Suspense keeps the current page visible
 * while the next chunk loads (no blank Loader flash).
 */
export function PrefetchLink({
  LinkComponent = Link,
  to,
  onClick,
  onPointerEnter,
  onFocus,
  onMouseDown,
  replace,
  state,
  target,
  ...rest
}: PrefetchLinkProps) {
  const navigate = useNavigate();

  const warm = () => {
    prefetchRoute(pathFromTo(to));
  };

  const handlePointerEnter: PointerEventHandler<HTMLAnchorElement> = (e) => {
    warm();
    onPointerEnter?.(e);
  };

  const handleFocus: FocusEventHandler<HTMLAnchorElement> = (e) => {
    warm();
    onFocus?.(e);
  };

  const handleMouseDown: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (e.button === 0) warm();
    onMouseDown?.(e);
  };

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (target === '_blank') return;
    e.preventDefault();
    warm();
    startTransition(() => {
      navigate(to, { replace, state });
    });
  };

  return (
    <LinkComponent
      {...rest}
      to={to}
      replace={replace}
      state={state}
      target={target}
      onPointerEnter={handlePointerEnter}
      onFocus={handleFocus}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    />
  );
}
